import { create } from 'zustand';
import api from '../api/axios';

const useAuthStore = create((set, get) => ({
  user: null,
  permissions: {},
  roleSettings: {},
  isAuthenticated: false,
  isLoading: true,

  login: async (username, password) => {
    try {
      const response = await api.post('/auth/login/', { username, password });
      const { access, refresh, user, permissions, role_settings } = response.data;
      
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      
      set({
        user,
        permissions,
        roleSettings: role_settings || {},
        isAuthenticated: true,
        isLoading: false,
      });
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  },

  logout: async () => {
    try {
      const refresh = localStorage.getItem('refresh_token');
      await api.post('/auth/logout/', { refresh });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    
    set({
      user: null,
      permissions: {},
      roleSettings: {},
      isAuthenticated: false,
      isLoading: false,
    });
  },

  checkAuth: async () => {
    const token = localStorage.getItem('access_token');

    if (!token) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }

    try {
      const response = await api.get('/auth/me/');
      set({
        user: response.data.user,
        permissions: response.data.permissions,
        roleSettings: response.data.role_settings || {},
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      set({
        user: null,
        permissions: {},
        roleSettings: {},
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  refreshPermissions: async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const response = await api.get('/auth/me/');
      set({
        permissions: response.data.permissions,
        roleSettings: response.data.role_settings || {},
      });
    } catch (error) {
      // silent fail
    }
  },

  hasPermission: (module, action) => {
    const { user, permissions } = get();

    if (!user) return false;

    // Check by role_name (string) for admin/super_admin
    const roleName = user.role_name?.toLowerCase();
    if (roleName === 'super_admin' || roleName === 'admin') {
      return true;
    }

    const modulePerms = permissions[module];
    return modulePerms && modulePerms.includes(action);
  },

  isAdmin: () => {
    const { user } = get();
    const roleName = user?.role_name?.toLowerCase();
    return roleName === 'super_admin' || roleName === 'admin';
  },

  isManager: () => {
    const { user } = get();
    return user?.role_name?.toLowerCase() === 'manager';
  },

  isCashier: () => {
    const { user } = get();
    return user?.role_name?.toLowerCase() === 'cashier';
  },

  isStaff: () => {
    const { user } = get();
    return user?.role_name?.toLowerCase() === 'staff';
  },
}));

export default useAuthStore;
